import React, { useState, useEffect } from 'react';
import { 
  CreditCard, TrendingUp, DollarSign, Users, CheckCircle, AlertCircle, 
  Clock, Ban, FileText, RefreshCw, Search, Filter, Calendar,
  AlertTriangle, Building, Eye, Lock, Unlock, Trash2, Wrench, Edit
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '../../utils/dateTimeUtils';

const SubscriptionDashboard = () => {
  const [report, setReport] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showFixModal, setShowFixModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showInvoiceFixModal, setShowInvoiceFixModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceFixAmount, setInvoiceFixAmount] = useState('');
  const [invoiceFixPlanType, setInvoiceFixPlanType] = useState('');
  const [fixForm, setFixForm] = useState({
    plan_code: 'premium',
    billing_cycle: 'yearly'
  });
  const [setupForm, setSetupForm] = useState({
    plan_code: 'premium',
    plan_type: 'monthly',
    amount: 2499,
    currency: 'INR',
    grace_period_days: 7
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [reportRes, invoicesRes, plansRes, groupInvRes] = await Promise.all([
        axios.get(`${API_URL}/api/subscriptions/report`, { headers }),
        axios.get(`${API_URL}/api/subscriptions/all-invoices`, { headers }),
        axios.get(`${API_URL}/api/tenants/subscription-plans/list`, { headers }),
        // Group invoices are stored in a separate collection — merge them in.
        axios.get(`${API_URL}/api/groups/all-invoices`, { headers }).catch(() => ({ data: { invoices: [] } })),
      ]);

      setReport(reportRes.data);
      const tenantInvoices = (invoicesRes.data.invoices || []).map(inv => ({ ...inv, is_group: false }));
      const groupInvoices = (groupInvRes.data.invoices || []).map(inv => ({
        ...inv,
        is_group: true,
        // Normalise field names so the same table can render both shapes
        tenant_name: inv.tenant_name || inv.group_name,
        tenant_id: inv.tenant_id || inv.group_id,
      }));
      // Sort merged list by created_at desc
      const merged = [...tenantInvoices, ...groupInvoices].sort((a, b) =>
        new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
      setInvoices(merged);
      setPlans(plansRes.data || []);
      
      // Set default plan amount from first premium plan
      const premiumPlan = plansRes.data?.find(p => p.code === 'premium' || p.name === 'premium');
      if (premiumPlan) {
        setSetupForm(prev => ({
          ...prev,
          plan_code: premiumPlan.code || premiumPlan.name,
          amount: premiumPlan.price_monthly || premiumPlan.price || 2499
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetupSubscription = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/subscriptions/setup-tenant/${selectedTenant.id}`,
        setupForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Subscription setup successfully!');
      setShowSetupModal(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to setup subscription');
    }
  };

  const handleBlockTenant = async (tenantId, block = true) => {
    const action = block ? 'block' : 'unblock';
    if (!window.confirm(`Are you sure you want to ${action} this tenant?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/subscriptions/${action}-tenant/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Tenant ${action}ed successfully!`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || `Failed to ${action} tenant`);
    }
  };

  const handleGenerateInvoice = async (tenantId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/subscriptions/generate-invoice/${tenantId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Invoice generated successfully!');
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to generate invoice');
    }
  };

  const handleMarkAsPaid = async (invoiceId, invoiceNumber, isGroup = false) => {
    const subjectLabel = isGroup ? 'group' : 'tenant';
    if (!window.confirm(`Are you sure you want to mark invoice ${invoiceNumber} as PAID?\n\nThis will:\n- Update invoice status to Paid\n- Activate the ${subjectLabel} subscription\n- Extend subscription end date`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      // Group invoices live in db.group_invoices and have their own endpoint.
      const url = isGroup
        ? `${API_URL}/api/groups/invoice/${invoiceId}/mark-paid`
        : `${API_URL}/api/subscriptions/mark-invoice-paid/${invoiceId}`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(`✅ ${res.data.message}${res.data.new_end_date ? `\n\nNew subscription end date: ${res.data.new_end_date}` : ''}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to mark invoice as paid');
    }
  };

  // Fix subscription data for a tenant
  const handleFixSubscription = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/tenants/${selectedTenant.id}/fix-subscription`,
        fixForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ Subscription fixed! Plan: ${res.data.plan_code}, Billing: ${res.data.billing_cycle}, Amount: ₹${res.data.amount}`);
      setShowFixModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix subscription');
    }
  };

  const openFixModal = (tenant) => {
    setSelectedTenant(tenant);
    setFixForm({
      plan_code: tenant.subscription_plan || tenant.subscription?.plan_code || 'premium',
      billing_cycle: tenant.billing_cycle || tenant.subscription?.plan_type || 'monthly'
    });
    setShowFixModal(true);
  };

  // Open invoice fix modal
  const openInvoiceFixModal = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceFixAmount(invoice.amount || '');
    setInvoiceFixPlanType(invoice.plan_type || 'monthly');
    setShowInvoiceFixModal(true);
  };

  // Fix invoice amount and plan type
  const handleFixInvoiceAmount = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put(
        `${API_URL}/api/subscriptions/invoice/${selectedInvoice.id}/fix-amount`,
        { 
          amount: parseFloat(invoiceFixAmount),
          plan_type: invoiceFixPlanType 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`✅ ${res.data.message}`);
      setShowInvoiceFixModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fix invoice');
    }
  };

  const handleDeleteInvoice = async (invoiceId, invoiceNumber, isGroup = false) => {
    if (!window.confirm(`Are you sure you want to DELETE invoice ${invoiceNumber}?\n\n⚠️ This action cannot be undone!`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const url = isGroup
        ? `${API_URL}/api/groups/invoice/${invoiceId}`
        : `${API_URL}/api/subscriptions/invoice/${invoiceId}`;
      await axios.delete(url, { headers: { Authorization: `Bearer ${token}` } });
      alert(`✅ Invoice ${invoiceNumber} deleted successfully!`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleCheckExpirations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/subscriptions/check-expirations`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Expiration check completed!\nInvoices Generated: ${res.data.results.invoices_generated}\nTenants in Grace: ${res.data.results.tenants_in_grace}\nTenants Blocked: ${res.data.results.tenants_blocked}`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to check expirations');
    }
  };

  const getStatusBadge = (status, isBlocked) => {
    if (isBlocked) return { color: 'bg-red-100 text-red-800 border-red-300', icon: Ban, text: 'Blocked' };
    const badges = {
      active: { color: 'bg-green-100 text-green-800 border-green-300', icon: CheckCircle, text: 'Active' },
      grace_period: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', icon: Clock, text: 'Grace Period' },
      expired: { color: 'bg-orange-100 text-orange-800 border-orange-300', icon: AlertTriangle, text: 'Expired' },
      blocked: { color: 'bg-red-100 text-red-800 border-red-300', icon: Ban, text: 'Blocked' }
    };
    return badges[status] || badges.active;
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    return Math.ceil((end - today) / (1000 * 60 * 60 * 24));
  };

  const filteredTenants = report?.tenants?.filter(t => {
    const subscription = t.subscription || {};
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter || 
                          (statusFilter === 'blocked' && subscription.is_blocked);
    const matchesSearch = t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          t.subdomain?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="subscription-dashboard">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Subscription Dashboard</h1>
          <p className="text-gray-600 mt-1">Monitor and manage tenant subscriptions</p>
        </div>
        <button
          onClick={handleCheckExpirations}
          className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
          data-testid="check-expirations-btn"
        >
          <RefreshCw className="h-5 w-5" />
          Check Expirations
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Tenants</p>
              <p className="text-2xl font-bold text-gray-900">{report?.summary?.total_tenants || 0}</p>
            </div>
            <Building className="h-10 w-10 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Subscriptions</p>
              <p className="text-2xl font-bold text-green-600">{report?.summary?.active_tenants || 0}</p>
            </div>
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Amount</p>
              <p className="text-2xl font-bold text-orange-600">₹{(report?.summary?.total_pending_amount || 0).toLocaleString('en-IN')}</p>
            </div>
            <Clock className="h-10 w-10 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">This Month Collection</p>
              <p className="text-2xl font-bold text-purple-600">₹{(report?.summary?.month_collection || 0).toLocaleString('en-IN')}</p>
            </div>
            <TrendingUp className="h-10 w-10 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Alert Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-center gap-3">
          <Clock className="h-8 w-8 text-yellow-600" />
          <div>
            <p className="text-sm text-yellow-800 font-medium">Pending Invoices</p>
            <p className="text-xl font-bold text-yellow-900">{report?.summary?.pending_invoices || 0}</p>
          </div>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <Ban className="h-8 w-8 text-red-600" />
          <div>
            <p className="text-sm text-red-800 font-medium">Blocked Tenants</p>
            <p className="text-xl font-bold text-red-900">{report?.summary?.blocked_tenants || 0}</p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-orange-600" />
          <div>
            <p className="text-sm text-orange-800 font-medium">Expired Subscriptions</p>
            <p className="text-xl font-bold text-orange-900">{report?.summary?.expired_tenants || 0}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="border-b flex">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 font-medium ${activeTab === 'overview' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            Tenants Overview
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            className={`px-6 py-3 font-medium ${activeTab === 'invoices' ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-600'}`}
          >
            All Invoices
          </button>
        </div>

        {activeTab === 'overview' && (
          <div className="p-4">
            {/* Filters */}
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search tenant..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                  />
                </div>
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="grace_period">Grace Period</option>
                <option value="expired">Expired</option>
                <option value="blocked">Blocked</option>
              </select>
            </div>

            {/* Tenants Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold text-gray-700">Tenant</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Plan</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Validity</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-8 text-gray-500">
                        No tenants found
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((tenant) => {
                      const subscription = tenant.subscription || {};
                      const statusBadge = getStatusBadge(subscription.status, subscription.is_blocked);
                      const daysRemaining = getDaysRemaining(subscription.end_date);

                      return (
                        <tr key={tenant.id} className="hover:bg-gray-50">
                          <td className="p-4">
                            <div>
                              <p className="font-medium text-gray-900">{tenant.name}</p>
                              <p className="text-sm text-gray-500">{tenant.subdomain}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            {(tenant.subscription_plan || subscription.plan_name || subscription.plan_type) ? (
                              <div>
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium capitalize">
                                  {subscription.plan_name || tenant.subscription_plan || 'N/A'}
                                </span>
                                {subscription.plan_type && (
                                  <span className="ml-1 text-xs text-gray-500 capitalize">
                                    ({subscription.plan_type})
                                  </span>
                                )}
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedTenant(tenant);
                                  setShowSetupModal(true);
                                }}
                                className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm hover:bg-gray-200"
                              >
                                + Setup
                              </button>
                            )}
                          </td>
                          <td className="p-4">
                            <span className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border w-fit ${statusBadge.color}`}>
                              <statusBadge.icon className="h-3.5 w-3.5" />
                              {statusBadge.text}
                            </span>
                          </td>
                          <td className="p-4">
                            {subscription.end_date ? (
                              <div>
                                <p className="text-sm">{formatDate(subscription.end_date)}</p>
                                <p className={`text-xs ${daysRemaining > 7 ? 'text-green-600' : daysRemaining > 0 ? 'text-orange-600' : 'text-red-600'}`}>
                                  {daysRemaining > 0 ? `${daysRemaining} days left` : `${Math.abs(daysRemaining)} days overdue`}
                                </p>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            {subscription.amount ? (
                              <span className="font-medium">₹{subscription.amount.toLocaleString('en-IN')}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="flex gap-2">
                              {subscription.plan_type && (
                                <>
                                  <button
                                    onClick={() => {
                                      // Switch to Invoices tab and filter by this tenant
                                      setActiveTab('invoices');
                                      setSearchTerm(tenant.name);
                                    }}
                                    className="p-1.5 bg-blue-100 text-blue-600 rounded hover:bg-blue-200"
                                    title="View Invoices"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleGenerateInvoice(tenant.id)}
                                    className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200"
                                    title="Generate New Invoice"
                                  >
                                    <FileText className="h-4 w-4" />
                                  </button>
                                  {subscription.is_blocked ? (
                                    <button
                                      onClick={() => handleBlockTenant(tenant.id, false)}
                                      className="p-1.5 bg-green-100 text-green-600 rounded hover:bg-green-200"
                                      title="Unblock Tenant"
                                    >
                                      <Unlock className="h-4 w-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleBlockTenant(tenant.id, true)}
                                      className="p-1.5 bg-red-100 text-red-600 rounded hover:bg-red-200"
                                      title="Block Tenant"
                                    >
                                      <Lock className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openFixModal(tenant)}
                                    className="p-1.5 bg-amber-100 text-amber-600 rounded hover:bg-amber-200"
                                    title="Fix Subscription Data"
                                  >
                                    <Wrench className="h-4 w-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'invoices' && (
          <div className="p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-semibold text-gray-700">Invoice #</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Tenant</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Period</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Amount</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Due Date</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Payment Date</th>
                    <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-8 text-gray-500">
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="p-4 font-mono text-sm">{invoice.invoice_number}</td>
                        <td className="p-4">
                          {invoice.tenant_name}
                          {invoice.is_group && (
                            <span className="ml-2 px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] rounded font-semibold align-middle" data-testid={`group-invoice-badge-${invoice.id}`}>
                              GROUP
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-sm">
                          {invoice.billing_period_start} to {invoice.billing_period_end}
                        </td>
                        <td className="p-4 font-medium">
                          <div data-testid={`invoice-amount-${invoice.id}`}>₹{invoice.amount?.toLocaleString('en-IN')}</div>
                          {(invoice.gst_amount > 0 || (invoice.gst_percent > 0 && invoice.base_amount)) && (
                            <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                              <div>Base: ₹{Number(invoice.base_amount || 0).toLocaleString('en-IN')}</div>
                              <div>GST ({invoice.gst_percent || 0}%): ₹{Number(invoice.gst_amount || 0).toLocaleString('en-IN')}</div>
                            </div>
                          )}
                        </td>
                        <td className="p-4 text-sm">{invoice.due_date}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            invoice.status === 'paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'overdue' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {invoice.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="p-4 text-sm">
                          {invoice.payment_date ? formatDate(invoice.payment_date) : '-'}
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            {/* Fix-amount endpoint only exists for tenant invoices */}
                            {!invoice.is_group && (
                              <button
                                onClick={() => openInvoiceFixModal(invoice)}
                                className="px-2 py-1.5 bg-amber-100 text-amber-600 text-xs rounded hover:bg-amber-200 flex items-center gap-1"
                                title="Fix Invoice Amount"
                              >
                                <Wrench className="h-3.5 w-3.5" />
                                Fix
                              </button>
                            )}
                            {invoice.status !== 'paid' && (
                              <button
                                onClick={() => handleMarkAsPaid(invoice.id, invoice.invoice_number, invoice.is_group)}
                                className="px-3 py-1.5 bg-green-600 text-white text-xs rounded hover:bg-green-700 flex items-center gap-1"
                                title="Mark as Paid"
                                data-testid={`mark-paid-btn-${invoice.id}`}
                              >
                                <CheckCircle className="h-3.5 w-3.5" />
                                Mark Paid
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteInvoice(invoice.id, invoice.invoice_number, invoice.is_group)}
                              className="px-2 py-1.5 bg-red-100 text-red-600 text-xs rounded hover:bg-red-200 flex items-center gap-1"
                              title="Delete Invoice"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Invoice Fix Modal */}
      {showInvoiceFixModal && selectedInvoice && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <Wrench className="h-6 w-6 text-amber-600" />
              <h2 className="text-xl font-bold">Fix Invoice Amount</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Fix amount for invoice <strong>{selectedInvoice.invoice_number}</strong>
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Tenant:</strong> {selectedInvoice.tenant_name}<br/>
                <strong>Current Amount:</strong> ₹{selectedInvoice.amount?.toLocaleString('en-IN')}
              </p>
            </div>

            <form onSubmit={handleFixInvoiceAmount}>
              {/* Plan Type Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {['monthly', 'quarterly', 'yearly'].map(type => (
                    <label 
                      key={type}
                      className={`flex items-center justify-center p-2 border rounded-lg cursor-pointer transition-all ${
                        invoiceFixPlanType === type 
                          ? 'border-amber-500 bg-amber-50 text-amber-700' 
                          : 'border-gray-200 hover:border-amber-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="planType"
                        value={type}
                        checked={invoiceFixPlanType === type}
                        onChange={(e) => setInvoiceFixPlanType(e.target.value)}
                        className="sr-only"
                      />
                      <span className="capitalize font-medium">{type}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  ⚠️ Plan type determines subscription duration when marking as paid
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                <input
                  type="number"
                  value={invoiceFixAmount}
                  onChange={(e) => setInvoiceFixAmount(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-lg font-semibold"
                  placeholder="Enter correct amount"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Common amounts: ₹2,499 (Premium Monthly), ₹24,999 (Premium Yearly), ₹999 (Standard Monthly), ₹9,999 (Standard Yearly)
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 font-medium"
                >
                  Fix Invoice
                </button>
                <button
                  type="button"
                  onClick={() => setShowInvoiceFixModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Setup Subscription Modal */}
      {showSetupModal && selectedTenant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <h2 className="text-xl font-bold mb-4">Setup Subscription</h2>
            <p className="text-gray-600 mb-4">Configure subscription for <strong>{selectedTenant.name}</strong></p>
            
            <form onSubmit={handleSetupSubscription}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Select Plan *</label>
                  <select
                    value={setupForm.plan_code}
                    onChange={(e) => {
                      const selectedPlan = plans.find(p => (p.code || p.name) === e.target.value);
                      if (selectedPlan) {
                        const amount = setupForm.plan_type === 'yearly' 
                          ? (selectedPlan.price_yearly || selectedPlan.price_monthly * 12)
                          : (selectedPlan.price_monthly || selectedPlan.price || 0);
                        setSetupForm({ 
                          ...setupForm, 
                          plan_code: e.target.value,
                          amount 
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {plans.map(plan => (
                      <option key={plan.id || plan.code} value={plan.code || plan.name}>
                        {plan.display_name || plan.name} - ₹{(plan.price_monthly || plan.price || 0).toLocaleString('en-IN')}/mo
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle *</label>
                  <select
                    value={setupForm.plan_type}
                    onChange={(e) => {
                      const type = e.target.value;
                      const selectedPlan = plans.find(p => (p.code || p.name) === setupForm.plan_code);
                      let amount = selectedPlan?.price_monthly || 0;
                      if (type === 'yearly') {
                        amount = selectedPlan?.price_yearly || (selectedPlan?.price_monthly * 12) || 0;
                      } else if (type === 'quarterly') {
                        amount = (selectedPlan?.price_monthly * 3) || 0;
                      }
                      setSetupForm({ ...setupForm, plan_type: type, amount });
                    }}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly (3 months)</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                  <input
                    type="number"
                    value={setupForm.amount}
                    onChange={(e) => setSetupForm({ ...setupForm, amount: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-calculated based on plan. Override if needed.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grace Period (Days)</label>
                  <input
                    type="number"
                    value={setupForm.grace_period_days}
                    onChange={(e) => setSetupForm({ ...setupForm, grace_period_days: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700"
                >
                  Setup Subscription
                </button>
                <button
                  type="button"
                  onClick={() => setShowSetupModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Fix Subscription Modal */}
      {showFixModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-2 mb-4">
              <Wrench className="h-6 w-6 text-amber-600" />
              <h2 className="text-xl font-bold">Fix Subscription</h2>
            </div>
            <p className="text-gray-600 mb-4">
              Fix subscription data for <strong>{selectedTenant.name}</strong>
            </p>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-amber-800">
                <strong>Current:</strong> {selectedTenant.subscription?.plan_type || 'Unknown'} - 
                ₹{selectedTenant.subscription?.amount?.toLocaleString('en-IN') || '0'}
              </p>
            </div>

            <form onSubmit={handleFixSubscription}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
                  <select
                    value={fixForm.plan_code}
                    onChange={(e) => setFixForm({ ...fixForm, plan_code: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg"
                  >
                    {plans.map(plan => (
                      <option key={plan.id || plan.code} value={plan.code}>
                        {plan.name} - Monthly: ₹{plan.price_monthly?.toLocaleString('en-IN')}, Yearly: ₹{plan.price_yearly?.toLocaleString('en-IN')}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Billing Cycle</label>
                  <div className="flex gap-4">
                    <label className={`flex-1 p-3 border rounded-lg cursor-pointer text-center ${fixForm.billing_cycle === 'monthly' ? 'border-blue-500 bg-blue-50' : ''}`}>
                      <input
                        type="radio"
                        name="billing"
                        value="monthly"
                        checked={fixForm.billing_cycle === 'monthly'}
                        onChange={(e) => setFixForm({ ...fixForm, billing_cycle: e.target.value })}
                        className="sr-only"
                      />
                      <span className="font-medium">Monthly</span>
                    </label>
                    <label className={`flex-1 p-3 border rounded-lg cursor-pointer text-center ${fixForm.billing_cycle === 'yearly' ? 'border-blue-500 bg-blue-50' : ''}`}>
                      <input
                        type="radio"
                        name="billing"
                        value="yearly"
                        checked={fixForm.billing_cycle === 'yearly'}
                        onChange={(e) => setFixForm({ ...fixForm, billing_cycle: e.target.value })}
                        className="sr-only"
                      />
                      <span className="font-medium">Yearly</span>
                    </label>
                  </div>
                </div>

                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">
                    <strong>New Amount:</strong> ₹
                    {(() => {
                      const plan = plans.find(p => p.code === fixForm.plan_code);
                      if (!plan) return '0';
                      return fixForm.billing_cycle === 'yearly' 
                        ? (plan.price_yearly || plan.price_monthly * 10)?.toLocaleString('en-IN')
                        : plan.price_monthly?.toLocaleString('en-IN');
                    })()}
                  </p>
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700 font-medium"
                >
                  Fix Subscription
                </button>
                <button
                  type="button"
                  onClick={() => setShowFixModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDashboard;
